import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, Menu, User, LogOut, Settings as SettingsIcon, Bell,
    Plus, Calendar, Clock, Repeat, Users, Edit, Trash2, Check, X,
    Search, Filter, Heart, Play, Image as ImageIcon, Link as LinkIcon,
    Camera, Mic, FileText, Tag, Star, TrendingUp, Gift, CheckCircle,
    AlertCircle, Book, Sparkles, ChevronRight, ChevronLeft, Volume2,
    Video, Award, Flame, Crown
} from 'lucide-react';
import { fetchBeliefs, createBelief, updateBelief, deleteBelief } from '../services/beliefsApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const BeliefsAndTraditions = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('rituals'); // rituals, festivals, wisdom
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    // Rituals state
    const [rituals, setRituals] = useState([]);
    const [showAddRitualModal, setShowAddRitualModal] = useState(false);
    const [editingRitual, setEditingRitual] = useState(null);
    const [ritualSearchQuery, setRitualSearchQuery] = useState('');
    const [newRitual, setNewRitual] = useState({
        name: '',
        type: 'Religious',
        frequency: 'Daily',
        customFrequency: '',
        timeOfDay: '',
        notes: '',
        instructions: '',
        imageUrl: '',
        videoUrl: '',
        assignedMembers: [],
        reminderEnabled: false,
        reminderTime: '',
        completed: [],
        streak: 0
    });

    // Festivals state
    const [festivals, setFestivals] = useState([]);
    const [showAddFestivalModal, setShowAddFestivalModal] = useState(false);
    const [editingFestival, setEditingFestival] = useState(null);
    const [festivalSearchQuery, setFestivalSearchQuery] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [newFestival, setNewFestival] = useState({
        title: '',
        date: '',
        description: '',
        rituals: '',
        preparationTasks: [],
        shoppingList: [],
        photos: [],
        recipes: '',
        decorationTasks: [],
        reminderDays: 2,
        recurring: true,
        category: 'Religious'
    });

    // Wisdom state
    const [wisdoms, setWisdoms] = useState([]);
    const [showAddWisdomModal, setShowAddWisdomModal] = useState(false);
    const [editingWisdom, setEditingWisdom] = useState(null);
    const [wisdomSearchQuery, setWisdomSearchQuery] = useState('');
    const [selectedWisdomCategory, setSelectedWisdomCategory] = useState('All');
    const [showWisdomOfTheDay, setShowWisdomOfTheDay] = useState(true);
    const [newWisdom, setNewWisdom] = useState({
        title: '',
        category: 'Life Lesson',
        description: '',
        audioUrl: '',
        videoUrl: '',
        tags: [],
        contributor: '',
        dateAdded: new Date().toISOString()
    });

    // Load families
    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load beliefs when family changes
    useEffect(() => {
        const loadBeliefsData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchBeliefs(selectedFamily.id);
                const beliefs = response.items || [];
                
                // Separate by type/category
                setRituals(beliefs.filter(b => b.type === 'ritual'));
                setFestivals(beliefs.filter(b => b.type === 'festival'));
                setWisdoms(beliefs.filter(b => b.type === 'wisdom'));
            } catch (error) {
                console.error('Error loading beliefs:', error);
            }
        };
        loadBeliefsData();
        updateStreaks();
    }, [selectedFamily]);

    const saveRituals = (data) => {
        setRituals(data);
    };

    const saveFestivals = (data) => {
        setFestivals(data);
    };

    const saveWisdoms = (data) => {
        setWisdoms(data);
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id);
        setShowFamilyDropdown(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update streaks for rituals
    const updateStreaks = () => {
        const stored = localStorage.getItem('rituals');
        if (!stored) return;
        
        const rituals = JSON.parse(stored);
        const today = new Date().toDateString();
        
        rituals.forEach(ritual => {
            if (!ritual.completed) ritual.completed = [];
            
            // Check if completed today
            const completedToday = ritual.completed.some(date => new Date(date).toDateString() === today);
            
            if (!completedToday && ritual.frequency === 'Daily') {
                // Check if completed yesterday
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const completedYesterday = ritual.completed.some(
                    date => new Date(date).toDateString() === yesterday.toDateString()
                );
                
                if (!completedYesterday && ritual.streak > 0) {
                    ritual.streak = 0; // Break streak if not completed yesterday
                }
            }
        });
    };

    // Ritual functions
    const handleAddRitual = async () => {
        if (!newRitual.name.trim()) {
            alert('Please enter a ritual name');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            const beliefData = {
                type: 'ritual',
                name: newRitual.name,
                category: newRitual.type,
                frequency: newRitual.frequency,
                custom_frequency: newRitual.customFrequency || null,
                time_of_day: newRitual.timeOfDay || null,
                notes: newRitual.notes || null,
                instructions: newRitual.instructions || null,
                image_url: newRitual.imageUrl || null,
                video_url: newRitual.videoUrl || null,
                assigned_members: newRitual.assignedMembers,
                reminder_enabled: newRitual.reminderEnabled,
                reminder_time: newRitual.reminderTime || null,
                data: { completed: [], streak: 0 }
            };

            const created = await createBelief(selectedFamily.id, beliefData);
            saveRituals([...rituals, created]);
            resetRitualForm();
            setShowAddRitualModal(false);
        } catch (error) {
            console.error('Error adding ritual:', error);
            alert(`Failed to add ritual: ${error.message}`);
        }
    };

    const handleEditRitual = (ritual) => {
        setEditingRitual(ritual);
        setNewRitual(ritual);
        setShowAddRitualModal(true);
    };

    const handleUpdateRitual = async () => {
        if (!selectedFamily || !editingRitual) return;

        try {
            const beliefData = {
                type: 'ritual',
                name: newRitual.name,
                category: newRitual.type,
                frequency: newRitual.frequency,
                custom_frequency: newRitual.customFrequency || null,
                time_of_day: newRitual.timeOfDay || null,
                notes: newRitual.notes || null,
                instructions: newRitual.instructions || null,
                image_url: newRitual.imageUrl || null,
                video_url: newRitual.videoUrl || null,
                assigned_members: newRitual.assignedMembers,
                reminder_enabled: newRitual.reminderEnabled,
                reminder_time: newRitual.reminderTime || null,
                data: editingRitual.data || { completed: [], streak: 0 }
            };

            const updated = await updateBelief(selectedFamily.id, editingRitual.id, beliefData);
            saveRituals(rituals.map(r => r.id === editingRitual.id ? updated : r));
            resetRitualForm();
            setShowAddRitualModal(false);
            setEditingRitual(null);
        } catch (error) {
            console.error('Error updating ritual:', error);
            alert(`Failed to update ritual: ${error.message}`);
        }
    };

    const handleDeleteRitual = async (id) => {
        if (!window.confirm('Are you sure you want to delete this ritual?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteBelief(selectedFamily.id, id);
            saveRituals(rituals.filter(r => r.id !== id));
        } catch (error) {
            console.error('Error deleting ritual:', error);
            alert(`Failed to delete ritual: ${error.message}`);
        }
    };

    const handleCompleteRitual = (id) => {
        const updated = rituals.map(ritual => {
            if (ritual.id === id) {
                const today = new Date().toISOString();
                const completedToday = ritual.completed.some(date => 
                    new Date(date).toDateString() === new Date().toDateString()
                );
                
                if (!completedToday) {
                    const newCompleted = [...ritual.completed, today];
                    const newStreak = ritual.streak + 1;
                    return { ...ritual, completed: newCompleted, streak: newStreak };
                }
            }
            return ritual;
        });
        saveRituals(updated);
    };

    const resetRitualForm = () => {
        setNewRitual({
            name: '',
            type: 'Religious',
            frequency: 'Daily',
            customFrequency: '',
            timeOfDay: '',
            notes: '',
            instructions: '',
            imageUrl: '',
            videoUrl: '',
            assignedMembers: [],
            reminderEnabled: false,
            reminderTime: '',
            completed: [],
            streak: 0
        });
    };

    // Festival functions
    const handleAddFestival = async () => {
        if (!newFestival.title.trim() || !newFestival.date) {
            alert('Please enter festival title and date');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            const beliefData = {
                type: 'festival',
                name: newFestival.title,
                category: newFestival.category,
                date: newFestival.date,
                description: newFestival.description || null,
                data: {
                    rituals: newFestival.rituals,
                    preparationTasks: newFestival.preparationTasks || [],
                    shoppingList: newFestival.shoppingList || [],
                    photos: newFestival.photos || [],
                    recipes: newFestival.recipes,
                    decorationTasks: newFestival.decorationTasks || [],
                    reminderDays: newFestival.reminderDays,
                    recurring: newFestival.recurring
                }
            };

            const created = await createBelief(selectedFamily.id, beliefData);
            saveFestivals([...festivals, created]);
            resetFestivalForm();
            setShowAddFestivalModal(false);
        } catch (error) {
            console.error('Error adding festival:', error);
            alert(`Failed to add festival: ${error.message}`);
        }
    };

    const handleEditFestival = (festival) => {
        setEditingFestival(festival);
        setNewFestival(festival);
        setShowAddFestivalModal(true);
    };

    const handleUpdateFestival = async () => {
        if (!selectedFamily || !editingFestival) return;

        try {
            const beliefData = {
                type: 'festival',
                name: newFestival.title,
                category: newFestival.category,
                date: newFestival.date,
                description: newFestival.description || null,
                data: {
                    rituals: newFestival.rituals,
                    preparationTasks: newFestival.preparationTasks || [],
                    shoppingList: newFestival.shoppingList || [],
                    photos: newFestival.photos || [],
                    recipes: newFestival.recipes,
                    decorationTasks: newFestival.decorationTasks || [],
                    reminderDays: newFestival.reminderDays,
                    recurring: newFestival.recurring
                }
            };

            const updated = await updateBelief(selectedFamily.id, editingFestival.id, beliefData);
            saveFestivals(festivals.map(f => f.id === editingFestival.id ? updated : f));
            resetFestivalForm();
            setShowAddFestivalModal(false);
            setEditingFestival(null);
        } catch (error) {
            console.error('Error updating festival:', error);
            alert(`Failed to update festival: ${error.message}`);
        }
    };

    const handleDeleteFestival = async (id) => {
        if (!window.confirm('Are you sure you want to delete this festival?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteBelief(selectedFamily.id, id);
            saveFestivals(festivals.filter(f => f.id !== id));
        } catch (error) {
            console.error('Error deleting festival:', error);
            alert(`Failed to delete festival: ${error.message}`);
        }
    };

    const resetFestivalForm = () => {
        setNewFestival({
            title: '',
            date: '',
            description: '',
            rituals: '',
            preparationTasks: [],
            shoppingList: [],
            photos: [],
            recipes: '',
            decorationTasks: [],
            reminderDays: 2,
            recurring: true,
            category: 'Religious'
        });
    };

    const addPreparationTask = () => {
        setNewFestival({
            ...newFestival,
            preparationTasks: [...newFestival.preparationTasks, { task: '', assignedTo: '', completed: false }]
        });
    };

    const addShoppingItem = () => {
        setNewFestival({
            ...newFestival,
            shoppingList: [...newFestival.shoppingList, { item: '', quantity: '', purchased: false }]
        });
    };

    const addDecorationTask = () => {
        setNewFestival({
            ...newFestival,
            decorationTasks: [...newFestival.decorationTasks, { task: '', completed: false }]
        });
    };

    // Wisdom functions
    const handleAddWisdom = async () => {
        if (!newWisdom.title.trim() || !newWisdom.description.trim()) {
            alert('Please enter wisdom title and description');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            const beliefData = {
                type: 'wisdom',
                name: newWisdom.title,
                category: newWisdom.category,
                description: newWisdom.description,
                data: {
                    audioUrl: newWisdom.audioUrl || null,
                    videoUrl: newWisdom.videoUrl || null,
                    tags: newWisdom.tags || [],
                    contributor: newWisdom.contributor || null,
                    hearts: 0
                }
            };

            const created = await createBelief(selectedFamily.id, beliefData);
            saveWisdoms([...wisdoms, created]);
            resetWisdomForm();
            setShowAddWisdomModal(false);
        } catch (error) {
            console.error('Error adding wisdom:', error);
            alert(`Failed to add wisdom: ${error.message}`);
        }
    };

    const handleEditWisdom = (wisdom) => {
        setEditingWisdom(wisdom);
        setNewWisdom(wisdom);
        setShowAddWisdomModal(true);
    };

    const handleUpdateWisdom = async () => {
        if (!selectedFamily || !editingWisdom) return;

        try {
            const beliefData = {
                type: 'wisdom',
                name: newWisdom.title,
                category: newWisdom.category,
                description: newWisdom.description,
                data: {
                    audioUrl: newWisdom.audioUrl || null,
                    videoUrl: newWisdom.videoUrl || null,
                    tags: newWisdom.tags || [],
                    contributor: newWisdom.contributor || null,
                    hearts: editingWisdom.data?.hearts || 0
                }
            };

            const updated = await updateBelief(selectedFamily.id, editingWisdom.id, beliefData);
            saveWisdoms(wisdoms.map(w => w.id === editingWisdom.id ? updated : w));
            resetWisdomForm();
            setShowAddWisdomModal(false);
            setEditingWisdom(null);
        } catch (error) {
            console.error('Error updating wisdom:', error);
            alert(`Failed to update wisdom: ${error.message}`);
        }
    };

    const handleDeleteWisdom = async (id) => {
        if (!window.confirm('Are you sure you want to delete this wisdom?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteBelief(selectedFamily.id, id);
            saveWisdoms(wisdoms.filter(w => w.id !== id));
        } catch (error) {
            console.error('Error deleting wisdom:', error);
            alert(`Failed to delete wisdom: ${error.message}`);
        }
    };

    const handleHeartWisdom = (id) => {
        const updated = wisdoms.map(w => {
            if (w.id === id) {
                return { ...w, hearts: (w.hearts || 0) + 1 };
            }
            return w;
        });
        saveWisdoms(updated);
    };

    const resetWisdomForm = () => {
        setNewWisdom({
            title: '',
            category: 'Life Lesson',
            description: '',
            audioUrl: '',
            videoUrl: '',
            tags: [],
            contributor: '',
            dateAdded: new Date().toISOString()
        });
    };

    const addTag = (tag) => {
        if (tag && !newWisdom.tags.includes(tag)) {
            setNewWisdom({ ...newWisdom, tags: [...newWisdom.tags, tag] });
        }
    };

    const removeTag = (tag) => {
        setNewWisdom({ ...newWisdom, tags: newWisdom.tags.filter(t => t !== tag) });
    };

    // Get wisdom of the day
    const getWisdomOfTheDay = () => {
        if (wisdoms.length === 0) return null;
        const today = new Date().toDateString();
        const storedDate = localStorage.getItem('wisdomOfTheDayDate');
        const storedIndex = localStorage.getItem('wisdomOfTheDayIndex');
        
        if (storedDate === today && storedIndex !== null) {
            return wisdoms[parseInt(storedIndex)];
        }
        
        const randomIndex = Math.floor(Math.random() * wisdoms.length);
        localStorage.setItem('wisdomOfTheDayDate', today);
        localStorage.setItem('wisdomOfTheDayIndex', randomIndex.toString());
        return wisdoms[randomIndex];
    };

    const wisdomOfTheDay = getWisdomOfTheDay();

    // Filter functions
    const filteredRituals = rituals.filter(ritual =>
        ritual.name.toLowerCase().includes(ritualSearchQuery.toLowerCase()) ||
        ritual.type.toLowerCase().includes(ritualSearchQuery.toLowerCase())
    );

    const filteredFestivals = festivals.filter(festival => {
        const matchesSearch = festival.title.toLowerCase().includes(festivalSearchQuery.toLowerCase());
        const festivalMonth = new Date(festival.date).getMonth();
        const matchesMonth = selectedMonth === -1 || festivalMonth === selectedMonth;
        return matchesSearch && matchesMonth;
    });

    const filteredWisdoms = wisdoms.filter(wisdom => {
        const matchesSearch = wisdom.title.toLowerCase().includes(wisdomSearchQuery.toLowerCase()) ||
                              wisdom.description.toLowerCase().includes(wisdomSearchQuery.toLowerCase()) ||
                              wisdom.tags.some(tag => tag.toLowerCase().includes(wisdomSearchQuery.toLowerCase()));
        const matchesCategory = selectedWisdomCategory === 'All' || wisdom.category === selectedWisdomCategory;
        return matchesSearch && matchesCategory;
    });

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50">
            {/* Navigation Bar */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-200">
                                        {families.map((family) => (
                                            <button
                                                key={family.id}
                                                onClick={() => handleFamilySelect(family)}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                            >
                                                {family.photo ? (
                                                    <img 
                                                        src={family.photo} 
                                                        alt={family.name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium">{family.name}</div>
                                                    {family.tagline && (
                                                        <div className="text-xs text-gray-500">{family.tagline}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-200 mt-2 pt-2">
                                            <button
                                                onClick={() => navigate('/famlis')}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-primary-blue font-medium"
                                            >
                                                + Add Family
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-primary-blue to-primary-pink flex items-center justify-center text-white font-semibold">
                                        U
                                    </div>
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 border border-gray-100 z-50">
                                        <button
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5" />
                                            <span className="font-medium">Profile</span>
                                        </button>
                                        <button
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <SettingsIcon className="w-5 h-5" />
                                            <span className="font-medium">Settings</span>
                                        </button>
                                        <button
                                            onClick={() => setShowPremiumModal(true)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Crown className="w-5 h-5 text-yellow-500" />
                                            <span className="font-medium">Upgrade to Premium</span>
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Beliefs & Traditions</span>
                </div>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">🕉️ Beliefs & Traditions</h1>
                    <p className="text-gray-600">Preserve and celebrate your family's spiritual and cultural heritage</p>
                </div>

                {/* Tabs */}
                <div className="mb-8 border-b border-gray-200">
                    <div className="flex space-x-8">
                        <button
                            onClick={() => setActiveTab('rituals')}
                            className={`pb-4 px-2 font-medium transition-colors relative ${
                                activeTab === 'rituals'
                                    ? 'text-primary-blue'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            🕉️ Rituals & Practices
                            {activeTab === 'rituals' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('festivals')}
                            className={`pb-4 px-2 font-medium transition-colors relative ${
                                activeTab === 'festivals'
                                    ? 'text-primary-blue'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            🎉 Festivals & Events
                            {activeTab === 'festivals' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('wisdom')}
                            className={`pb-4 px-2 font-medium transition-colors relative ${
                                activeTab === 'wisdom'
                                    ? 'text-primary-blue'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            🌾 Teach & Pass On
                            {activeTab === 'wisdom' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                    </div>
                </div>

                {/* RITUALS TAB */}
                {activeTab === 'rituals' && (
                    <div>
                        {/* Search and Add */}
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex-1 max-w-md">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search rituals..."
                                        value={ritualSearchQuery}
                                        onChange={(e) => setRitualSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    resetRitualForm();
                                    setEditingRitual(null);
                                    setShowAddRitualModal(true);
                                }}
                                className="ml-4 flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Add Ritual</span>
                            </button>
                        </div>

                        {/* Rituals Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredRituals.map((ritual) => {
                                const completedToday = ritual.completed?.some(date =>
                                    new Date(date).toDateString() === new Date().toDateString()
                                );

                                return (
                                    <div key={ritual.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                                        {ritual.imageUrl && (
                                            <img src={ritual.imageUrl} alt={ritual.name} className="w-full h-40 object-cover" />
                                        )}
                                        <div className="p-6">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-900 mb-1">{ritual.name}</h3>
                                                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                                        {ritual.type}
                                                    </span>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleEditRitual(ritual)}
                                                        className="p-1 hover:bg-gray-100 rounded"
                                                    >
                                                        <Edit className="w-4 h-4 text-gray-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteRitual(ritual.id)}
                                                        className="p-1 hover:bg-gray-100 rounded"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-600" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="space-y-2 mb-4">
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <Repeat className="w-4 h-4 mr-2" />
                                                    {ritual.frequency === 'Custom' ? ritual.customFrequency : ritual.frequency}
                                                </div>
                                                {ritual.timeOfDay && (
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <Clock className="w-4 h-4 mr-2" />
                                                        {ritual.timeOfDay}
                                                    </div>
                                                )}
                                                {ritual.streak > 0 && (
                                                    <div className="flex items-center text-sm text-orange-600 font-medium">
                                                        <Flame className="w-4 h-4 mr-2" />
                                                        {ritual.streak} day streak!
                                                    </div>
                                                )}
                                            </div>

                                            {ritual.notes && (
                                                <p className="text-sm text-gray-600 mb-4">{ritual.notes}</p>
                                            )}

                                            {ritual.videoUrl && (
                                                <a
                                                    href={ritual.videoUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center text-sm text-blue-600 hover:underline mb-4"
                                                >
                                                    <Video className="w-4 h-4 mr-1" />
                                                    Watch Video
                                                </a>
                                            )}

                                            <button
                                                onClick={() => handleCompleteRitual(ritual.id)}
                                                disabled={completedToday}
                                                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                                    completedToday
                                                        ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                                        : 'bg-purple-600 text-white hover:bg-purple-700'
                                                }`}
                                            >
                                                {completedToday ? (
                                                    <>
                                                        <CheckCircle className="w-5 h-5" />
                                                        <span>Completed Today</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Check className="w-5 h-5" />
                                                        <span>Mark as Done</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {filteredRituals.length === 0 && (
                            <div className="text-center py-12">
                                <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No rituals found</p>
                                <button
                                    onClick={() => setShowAddRitualModal(true)}
                                    className="text-purple-600 hover:underline"
                                >
                                    Add your first ritual
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* FESTIVALS TAB */}
                {activeTab === 'festivals' && (
                    <div>
                        {/* Search, Filter, and Add */}
                        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                            <div className="flex items-center space-x-4 flex-1">
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search festivals..."
                                        value={festivalSearchQuery}
                                        onChange={(e) => setFestivalSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    />
                                </div>
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                >
                                    <option value={-1}>All Months</option>
                                    {months.map((month, idx) => (
                                        <option key={idx} value={idx}>{month}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={() => {
                                    resetFestivalForm();
                                    setEditingFestival(null);
                                    setShowAddFestivalModal(true);
                                }}
                                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Add Festival</span>
                            </button>
                        </div>

                        {/* Festivals List */}
                        <div className="space-y-6">
                            {filteredFestivals.map((festival) => {
                                const festivalDate = new Date(festival.date);
                                const daysUntil = Math.ceil((festivalDate - new Date()) / (1000 * 60 * 60 * 24));
                                const isPast = daysUntil < 0;

                                return (
                                    <div key={festival.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                                        <div className="p-6">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <h3 className="text-2xl font-bold text-gray-900">{festival.title}</h3>
                                                        {!isPast && daysUntil <= 7 && (
                                                            <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-semibold rounded-full">
                                                                {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow!' : `In ${daysUntil} days`}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-gray-600 mb-2">
                                                        📅 {festivalDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                                    </p>
                                                    {festival.description && (
                                                        <p className="text-gray-700">{festival.description}</p>
                                                    )}
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleEditFestival(festival)}
                                                        className="p-2 hover:bg-gray-100 rounded"
                                                    >
                                                        <Edit className="w-5 h-5 text-gray-600" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteFestival(festival.id)}
                                                        className="p-2 hover:bg-gray-100 rounded"
                                                    >
                                                        <Trash2 className="w-5 h-5 text-red-600" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {festival.preparationTasks && festival.preparationTasks.length > 0 && (
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                                            <CheckCircle className="w-4 h-4 mr-2" />
                                                            Preparation Tasks
                                                        </h4>
                                                        <ul className="space-y-1">
                                                            {festival.preparationTasks.map((task, idx) => (
                                                                <li key={idx} className="text-sm text-gray-700 flex items-center">
                                                                    <input type="checkbox" checked={task.completed} readOnly className="mr-2" />
                                                                    {task.task}
                                                                    {task.assignedTo && <span className="ml-2 text-gray-500">({task.assignedTo})</span>}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {festival.shoppingList && festival.shoppingList.length > 0 && (
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                                            <Gift className="w-4 h-4 mr-2" />
                                                            Shopping List
                                                        </h4>
                                                        <ul className="space-y-1">
                                                            {festival.shoppingList.map((item, idx) => (
                                                                <li key={idx} className="text-sm text-gray-700 flex items-center">
                                                                    <input type="checkbox" checked={item.purchased} readOnly className="mr-2" />
                                                                    {item.item} {item.quantity && `(${item.quantity})`}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>

                                            {festival.rituals && (
                                                <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                                                    <h4 className="font-semibold text-gray-900 mb-2">Rituals</h4>
                                                    <p className="text-sm text-gray-700">{festival.rituals}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {filteredFestivals.length === 0 && (
                            <div className="text-center py-12">
                                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No festivals found</p>
                                <button
                                    onClick={() => setShowAddFestivalModal(true)}
                                    className="text-orange-600 hover:underline"
                                >
                                    Add your first festival
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* WISDOM TAB */}
                {activeTab === 'wisdom' && (
                    <div>
                        {/* Wisdom of the Day */}
                        {wisdomOfTheDay && showWisdomOfTheDay && (
                            <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg p-6 mb-8 relative">
                                <button
                                    onClick={() => setShowWisdomOfTheDay(false)}
                                    className="absolute top-4 right-4 text-gray-600 hover:text-gray-800"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                                <div className="flex items-start space-x-4">
                                    <Sparkles className="w-8 h-8 text-orange-600 flex-shrink-0 mt-1" />
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">✨ Wisdom of the Day</h3>
                                        <p className="text-2xl font-semibold text-gray-800 mb-2">"{wisdomOfTheDay.title}"</p>
                                        <p className="text-gray-700 mb-3">{wisdomOfTheDay.description}</p>
                                        {wisdomOfTheDay.contributor && (
                                            <p className="text-sm text-gray-600 italic">— {wisdomOfTheDay.contributor}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Search, Filter, and Add */}
                        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                            <div className="flex items-center space-x-4 flex-1">
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search wisdom..."
                                        value={wisdomSearchQuery}
                                        onChange={(e) => setWisdomSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    />
                                </div>
                                <select
                                    value={selectedWisdomCategory}
                                    onChange={(e) => setSelectedWisdomCategory(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                >
                                    <option value="All">All Categories</option>
                                    <option value="Life Lesson">Life Lesson</option>
                                    <option value="Value">Value</option>
                                    <option value="Saying">Saying</option>
                                    <option value="Story">Story</option>
                                    <option value="Proverb">Proverb</option>
                                </select>
                            </div>
                            <button
                                onClick={() => {
                                    resetWisdomForm();
                                    setEditingWisdom(null);
                                    setShowAddWisdomModal(true);
                                }}
                                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <Plus className="w-5 h-5" />
                                <span>Add Wisdom</span>
                            </button>
                        </div>

                        {/* Wisdoms Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {filteredWisdoms.map((wisdom) => (
                                <div key={wisdom.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">{wisdom.title}</h3>
                                            <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                {wisdom.category}
                                            </span>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleEditWisdom(wisdom)}
                                                className="p-1 hover:bg-gray-100 rounded"
                                            >
                                                <Edit className="w-4 h-4 text-gray-600" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteWisdom(wisdom.id)}
                                                className="p-1 hover:bg-gray-100 rounded"
                                            >
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </button>
                                        </div>
                                    </div>

                                    <p className="text-gray-700 mb-4 italic">"{wisdom.description}"</p>

                                    {wisdom.contributor && (
                                        <p className="text-sm text-gray-600 mb-3">— {wisdom.contributor}</p>
                                    )}

                                    <div className="flex items-center justify-between mb-4">
                                        {wisdom.tags && wisdom.tags.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {wisdom.tags.map((tag, idx) => (
                                                    <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                                                        #{tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
                                        {wisdom.audioUrl && (
                                            <a
                                                href={wisdom.audioUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center text-sm text-blue-600 hover:underline"
                                            >
                                                <Volume2 className="w-4 h-4 mr-1" />
                                                Audio
                                            </a>
                                        )}
                                        {wisdom.videoUrl && (
                                            <a
                                                href={wisdom.videoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center text-sm text-blue-600 hover:underline"
                                            >
                                                <Video className="w-4 h-4 mr-1" />
                                                Video
                                            </a>
                                        )}
                                        <button
                                            onClick={() => handleHeartWisdom(wisdom.id)}
                                            className="flex items-center text-sm text-red-600 hover:text-red-700 ml-auto"
                                        >
                                            <Heart className="w-4 h-4 mr-1 fill-current" />
                                            {wisdom.hearts || 0}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filteredWisdoms.length === 0 && (
                            <div className="text-center py-12">
                                <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4">No wisdom found</p>
                                <button
                                    onClick={() => setShowAddWisdomModal(true)}
                                    className="text-yellow-600 hover:underline"
                                >
                                    Add your first wisdom
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add/Edit Ritual Modal */}
            {showAddRitualModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">
                                {editingRitual ? 'Edit Ritual' : 'Add New Ritual'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddRitualModal(false);
                                    setEditingRitual(null);
                                    resetRitualForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={newRitual.name}
                                    onChange={(e) => setNewRitual({ ...newRitual, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="e.g., Morning Prayer, Evening Aarti"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Type
                                    </label>
                                    <select
                                        value={newRitual.type}
                                        onChange={(e) => setNewRitual({ ...newRitual, type: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    >
                                        <option value="Religious">Religious</option>
                                        <option value="Cultural">Cultural</option>
                                        <option value="Spiritual">Spiritual</option>
                                        <option value="Ethical">Ethical</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Frequency
                                    </label>
                                    <select
                                        value={newRitual.frequency}
                                        onChange={(e) => setNewRitual({ ...newRitual, frequency: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    >
                                        <option value="Daily">Daily</option>
                                        <option value="Weekly">Weekly</option>
                                        <option value="Monthly">Monthly</option>
                                        <option value="Yearly">Yearly</option>
                                        <option value="Custom">Custom</option>
                                    </select>
                                </div>
                            </div>

                            {newRitual.frequency === 'Custom' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Custom Frequency
                                    </label>
                                    <input
                                        type="text"
                                        value={newRitual.customFrequency}
                                        onChange={(e) => setNewRitual({ ...newRitual, customFrequency: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="e.g., Every Saturday, First Monday of month"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Time of Day
                                </label>
                                <input
                                    type="time"
                                    value={newRitual.timeOfDay}
                                    onChange={(e) => setNewRitual({ ...newRitual, timeOfDay: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Notes
                                </label>
                                <textarea
                                    value={newRitual.notes}
                                    onChange={(e) => setNewRitual({ ...newRitual, notes: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Brief notes about this ritual"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Instructions
                                </label>
                                <textarea
                                    value={newRitual.instructions}
                                    onChange={(e) => setNewRitual({ ...newRitual, instructions: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Detailed instructions on how to perform this ritual"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Image URL
                                </label>
                                <input
                                    type="url"
                                    value={newRitual.imageUrl}
                                    onChange={(e) => setNewRitual({ ...newRitual, imageUrl: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="https://example.com/image.jpg"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Video URL (YouTube, Vimeo, etc.)
                                </label>
                                <input
                                    type="url"
                                    value={newRitual.videoUrl}
                                    onChange={(e) => setNewRitual({ ...newRitual, videoUrl: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="https://youtube.com/watch?v=..."
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="reminderEnabled"
                                    checked={newRitual.reminderEnabled}
                                    onChange={(e) => setNewRitual({ ...newRitual, reminderEnabled: e.target.checked })}
                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                                <label htmlFor="reminderEnabled" className="text-sm font-medium text-gray-700">
                                    Enable Reminder
                                </label>
                            </div>

                            {newRitual.reminderEnabled && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Reminder Time (minutes before)
                                    </label>
                                    <input
                                        type="number"
                                        value={newRitual.reminderTime}
                                        onChange={(e) => setNewRitual({ ...newRitual, reminderTime: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="e.g., 5, 10, 15"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowAddRitualModal(false);
                                    setEditingRitual(null);
                                    resetRitualForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingRitual ? handleUpdateRitual : handleAddRitual}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                {editingRitual ? 'Update' : 'Add'} Ritual
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Festival Modal */}
            {showAddFestivalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">
                                {editingFestival ? 'Edit Festival' : 'Add New Festival'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddFestivalModal(false);
                                    setEditingFestival(null);
                                    resetFestivalForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Festival Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={newFestival.title}
                                        onChange={(e) => setNewFestival({ ...newFestival, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        placeholder="e.g., Diwali, Christmas"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Date *
                                    </label>
                                    <input
                                        type="date"
                                        value={newFestival.date}
                                        onChange={(e) => setNewFestival({ ...newFestival, date: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={newFestival.description}
                                    onChange={(e) => setNewFestival({ ...newFestival, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    placeholder="What is this festival about?"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Rituals & Traditions
                                </label>
                                <textarea
                                    value={newFestival.rituals}
                                    onChange={(e) => setNewFestival({ ...newFestival, rituals: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    placeholder="Describe the rituals and traditions for this festival"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Preparation Tasks
                                </label>
                                <div className="space-y-2">
                                    {newFestival.preparationTasks.map((task, index) => (
                                        <div key={index} className="flex space-x-2">
                                            <input
                                                type="text"
                                                value={task.task}
                                                onChange={(e) => {
                                                    const updated = [...newFestival.preparationTasks];
                                                    updated[index].task = e.target.value;
                                                    setNewFestival({ ...newFestival, preparationTasks: updated });
                                                }}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                placeholder="Task description"
                                            />
                                            <input
                                                type="text"
                                                value={task.assignedTo}
                                                onChange={(e) => {
                                                    const updated = [...newFestival.preparationTasks];
                                                    updated[index].assignedTo = e.target.value;
                                                    setNewFestival({ ...newFestival, preparationTasks: updated });
                                                }}
                                                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                placeholder="Assigned to"
                                            />
                                            <button
                                                onClick={() => {
                                                    const updated = newFestival.preparationTasks.filter((_, i) => i !== index);
                                                    setNewFestival({ ...newFestival, preparationTasks: updated });
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={addPreparationTask}
                                        className="text-sm text-orange-600 hover:text-orange-700"
                                    >
                                        + Add Task
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Shopping List
                                </label>
                                <div className="space-y-2">
                                    {newFestival.shoppingList.map((item, index) => (
                                        <div key={index} className="flex space-x-2">
                                            <input
                                                type="text"
                                                value={item.item}
                                                onChange={(e) => {
                                                    const updated = [...newFestival.shoppingList];
                                                    updated[index].item = e.target.value;
                                                    setNewFestival({ ...newFestival, shoppingList: updated });
                                                }}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                placeholder="Item name"
                                            />
                                            <input
                                                type="text"
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    const updated = [...newFestival.shoppingList];
                                                    updated[index].quantity = e.target.value;
                                                    setNewFestival({ ...newFestival, shoppingList: updated });
                                                }}
                                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                placeholder="Qty"
                                            />
                                            <button
                                                onClick={() => {
                                                    const updated = newFestival.shoppingList.filter((_, i) => i !== index);
                                                    setNewFestival({ ...newFestival, shoppingList: updated });
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={addShoppingItem}
                                        className="text-sm text-orange-600 hover:text-orange-700"
                                    >
                                        + Add Item
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="recurring"
                                        checked={newFestival.recurring}
                                        onChange={(e) => setNewFestival({ ...newFestival, recurring: e.target.checked })}
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                    />
                                    <label htmlFor="recurring" className="text-sm font-medium text-gray-700">
                                        Recurring Yearly
                                    </label>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Reminder (days before)
                                    </label>
                                    <input
                                        type="number"
                                        value={newFestival.reminderDays}
                                        onChange={(e) => setNewFestival({ ...newFestival, reminderDays: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowAddFestivalModal(false);
                                    setEditingFestival(null);
                                    resetFestivalForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingFestival ? handleUpdateFestival : handleAddFestival}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                            >
                                {editingFestival ? 'Update' : 'Add'} Festival
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Wisdom Modal */}
            {showAddWisdomModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">
                                {editingWisdom ? 'Edit Wisdom' : 'Add New Wisdom'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddWisdomModal(false);
                                    setEditingWisdom(null);
                                    resetWisdomForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    value={newWisdom.title}
                                    onChange={(e) => setNewWisdom({ ...newWisdom, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    placeholder="e.g., Never waste food, Patience cooks the best meal"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category
                                </label>
                                <select
                                    value={newWisdom.category}
                                    onChange={(e) => setNewWisdom({ ...newWisdom, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                >
                                    <option value="Life Lesson">Life Lesson</option>
                                    <option value="Value">Value</option>
                                    <option value="Saying">Saying</option>
                                    <option value="Story">Story</option>
                                    <option value="Proverb">Proverb</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description / Story *
                                </label>
                                <textarea
                                    value={newWisdom.description}
                                    onChange={(e) => setNewWisdom({ ...newWisdom, description: e.target.value })}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    placeholder="Share the full story or explanation behind this wisdom"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contributor (Elder/Family Member)
                                </label>
                                <input
                                    type="text"
                                    value={newWisdom.contributor}
                                    onChange={(e) => setNewWisdom({ ...newWisdom, contributor: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    placeholder="e.g., Grandma, Grandfather, Dad"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Audio URL (Optional)
                                </label>
                                <input
                                    type="url"
                                    value={newWisdom.audioUrl}
                                    onChange={(e) => setNewWisdom({ ...newWisdom, audioUrl: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    placeholder="Elder speaking the wisdom (audio recording URL)"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Video URL (Optional)
                                </label>
                                <input
                                    type="url"
                                    value={newWisdom.videoUrl}
                                    onChange={(e) => setNewWisdom({ ...newWisdom, videoUrl: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                    placeholder="Video recording URL"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tags
                                </label>
                                <div className="flex space-x-2 mb-2">
                                    <input
                                        type="text"
                                        id="tagInput"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addTag(e.target.value.trim());
                                                e.target.value = '';
                                            }
                                        }}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                        placeholder="Type a tag and press Enter (e.g., kindness, patience)"
                                    />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {newWisdom.tags.map((tag, idx) => (
                                        <span
                                            key={idx}
                                            className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
                                        >
                                            #{tag}
                                            <button
                                                onClick={() => removeTag(tag)}
                                                className="ml-2 text-yellow-600 hover:text-yellow-800"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowAddWisdomModal(false);
                                    setEditingWisdom(null);
                                    resetWisdomForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingWisdom ? handleUpdateWisdom : handleAddWisdom}
                                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                            >
                                {editingWisdom ? 'Update' : 'Add'} Wisdom
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BeliefsAndTraditions;
